import type { MapSearchType, SearchHookLocation } from "@repo/maps";
import slugify from "slugify";
import type { DetailedListing } from "@/lib/types";
import { US_STATES_WITH_BOUNDING_BOX } from "@repo/utils";
export {
  cachedEstimateLocationFromMap,
  estimateLocationFromMap,
  gridPrecisionForZoom,
  roundCoord,
} from "./map-location";

const COUNTRY_LOCATION_DATA = {
  us: {
    name: "United States",
    center: { lat: 39.8283, lng: -98.5795 },
    bbox: [-124.848974, 24.396308, -66.885444, 49.384358],
  },
  ca: {
    name: "Canada",
    center: { lat: 56.1304, lng: -106.3468 },
    bbox: [-141.00187, 41.675105, -52.648099, 83.23324],
  },
  nz: {
    name: "New Zealand",
    center: { lat: -40.9006, lng: 174.886 },
    bbox: [166.509144, -47.286026, 178.517093, -34.389668],
  },
} as const;

export function getCountryLocation(
  countryCode: string,
): SearchHookLocation | undefined {
  const normalizedCode = countryCode.toLowerCase() as keyof typeof COUNTRY_LOCATION_DATA;
  const country = COUNTRY_LOCATION_DATA[normalizedCode];
  if (!country) return undefined;

  return {
    name: country.name,
    formattedLocation: country.name,
    address: country.name,
    context: {
      country: {
        name: country.name,
        code: normalizedCode.toUpperCase(),
      },
    },
    coords: country.center,
    bbox: [...country.bbox] as [number, number, number, number],
  };
}

/**
 * For US state-level routes, returns a synthetic SearchHookLocation built from
 * hardcoded bounding-box data — zero geocoding API calls.
 * Returns undefined for non-US countries or sub-state segments (county/city).
 */
export function resolveUSStateFromSlug(
  countryCode: string,
  segments: string[],
): SearchHookLocation | undefined {
  if (countryCode.toLowerCase() !== "us" || segments.length !== 1) return;

  const state = US_STATES_WITH_BOUNDING_BOX.find(
    (s) => s.slug === segments[0],
  );
  if (!state) return;

  const [west, south, east, north] = state.bbox;

  return {
    name: state.name,
    formattedLocation: state.name,
    address: `${state.name}, United States`,
    context: {
      country: { name: "United States", code: "US" },
      region: { name: state.name, code: state.code },
    },
    coords: {
      lat: (south + north) / 2,
      lng: (west + east) / 2,
    },
    bbox: [west, south, east, north] as [number, number, number, number],
  };
}

/**
 * URL structure: /businesses/{country}/{region}/{district}/{place}
 * Segment count after country determines the deepest location level.
 */
const SEGMENT_TYPE_MAP: Record<number, MapSearchType> = {
  1: "region",
  2: "district",
  3: "place",
};

export function inferLocationTypesFromSegments(
  segmentCount: number,
): MapSearchType[] | undefined {
  const type = SEGMENT_TYPE_MAP[segmentCount];
  return type ? [type] : undefined;
}

/**
 * Checks whether a geocoded location's context matches the URL segments
 * by comparing slugified context names at each hierarchy level.
 */
export function locationMatchesSegments(
  location: SearchHookLocation,
  segments: string[],
): boolean {
  const contextNames: (string | undefined)[] = [
    location.context.region?.name,
    location.context.district?.name,
    location.context.place?.name,
  ];

  for (let i = 0; i < segments.length; i++) {
    const expected = segments[i];
    const actual = contextNames[i];

    if (!actual) return false;
    if (slugify(actual, { lower: true }) !== expected) return false;
  }

  return true;
}

/**
 * Finds the best matching location from geocoding results, preferring
 * results whose context hierarchy matches the URL segments exactly.
 */
export function findBestLocationMatch(
  locations: SearchHookLocation[],
  segments: string[],
): SearchHookLocation | undefined {
  return (
    locations.find((loc) => locationMatchesSegments(loc, segments)) ??
    locations[0]
  );
}

export function buildLocationUrlSegments(
  location: SearchHookLocation,
  countryCode?: string,
): string | null {
  const components: string[] = [];

  const { country, region, district, place } = location.context;

  const finalCountryCode = countryCode ?? country?.code;

  if (finalCountryCode) {
    components.push(finalCountryCode.toLowerCase());
  }

  if (region) {
    components.push(slugify(region.name, { lower: true }));
  }

  if (district) {
    components.push(slugify(district.name, { lower: true }));
  }

  if (place) {
    components.push(slugify(place.name, { lower: true }));
  }

  if (components.length === 0) {
    return null;
  }

  return components.join("/");
}

/** A geocoder result as the country/state/county/city fields the API resolves to a subdivision. */
export function searchLocationToInput(location: SearchHookLocation) {
  const context = location.context;
  const countryCode = context.country?.code?.toUpperCase();
  const countryName = context.country?.name;
  if (!countryCode || !countryName) return null;
  return {
    countryCode,
    countryName,
    neighborhood: context.neighborhood?.name,
    city: context.place?.name ?? context.locality?.name,
    county: context.district?.name,
    state: context.region?.name,
  };
}

export function formatLocation(location: SearchHookLocation): string {
  const { country, region, district, place } = location.context;

  const output = [];

  if (place) {
    output.push(place.name);
  }

  if (!place && district) {
    output.push(district.name);
  }

  if (region) {
    output.push(region.name);
  }

  if (output.length === 0 && country) {
    output.push(country.name);
  }

  return output.join(", ");
}
export function formatAddress(
  address: Partial<
    NonNullable<NonNullable<DetailedListing["business"]>["address"]>
  >,
): string {
  const output = [];

  if (address.streetAddress1) {
    output.push(address.streetAddress1);
  }

  if (address.city) {
    output.push(address.city);
  }

  if (address.state) {
    output.push(address.state);
  }

  return output.join(", ");
}
